import styled, { css } from 'styled-components';

import React from 'react';
import { CloseIcon } from '@zimpel/common/components/Icon';
import type HTMLProps from '@zimpel/common/HTMLProps';
import Tooltip from '@zimpel/common/components/Tooltip';
import t from '@zimpel/common/helpers/i18n';

type ListProps = {
  kind?: 'squared' | 'inlined' | 'font-awesome' | 'inlined-with-comma'
}

const UnstyledList = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;

  > li {
    margin-bottom: 0;
    
    > *:last-child {
      margin-bottom: 0;
    }
  }
`;

const List = styled(UnstyledList)<ListProps>`
  ${({ kind }) => !kind && css`
    > li {
      padding: 10px;
      border-bottom: var(--border-box);
    }
  `}
  ${({ kind }) => kind === 'squared' && css`
    padding-left: 20px;
    list-style: square;

    > li:not(:last-child) {
      margin-bottom: 5px;
    }
  `}
  ${({ kind }) => kind === 'font-awesome' && css`

    /* This basically reimplements the fa-ul class of font awesome
       as we dont want to use styled components instead of classes */

    margin-left: 20px;

    > li {
      position: relative;
    }

    > li:not(:last-child) {
      margin-bottom: 10px;
    }
  `}
  ${({ kind }) => kind === 'inlined' && css`
    display: inline-flex;
    flex-flow: wrap;

    > li:not(:last-child) {
      margin-right: 5px;
    }
  `}
  ${({ kind }) => kind === 'inlined-with-comma' && css`
    display: inline-flex;
    flex-flow: wrap;

    > li {
      margin-right: 3px;
    }

    > li::after {
      content: ',';
    }

    > li:last-child::after {
      content: none;
    }
  `}
`;

type ListSelectionProps = {
  $boxless?: boolean;
};

const ListSelection = styled(UnstyledList)<ListSelectionProps>`
  > li {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    overflow: hidden;
    word-break: break-word;
    ${({ $boxless }) => $boxless && css`
      margin-bottom: 8px;
      line-height: 1.2;
    `}
    ${({ $boxless }) => !$boxless && css`
      margin-bottom: 3px;
      padding: 1px 6px;
      background-color: var(--color-background-dark-hover);
    `}

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

type ListSelectionItemProps = { onRemove: () => void; tooltip?: string } & HTMLProps & React.PropsWithChildren;

const ListSelectionItem: React.FC<ListSelectionItemProps> = ({
  onRemove, children, tooltip, ...rest
}) => (
  <li data-test="list-selection-item" {...rest}>
    <div>{children}</div>
    <Tooltip content={tooltip || t('list_selection_item.remove.tooltip')}>
      <CloseIcon
        onClick={onRemove}
        data-test="remove-list-selection"
        leftMargin
        rightMargin={false}
      />
    </Tooltip>
  </li>
);

export {
  UnstyledList, List, ListSelection, ListSelectionItem,
};
